import { useCallback, useEffect, useMemo } from "react";
import {
  filterExchangeSignals,
  getSignalsFiltersSlice,
  groupFilteredExchangeSignals,
  groupExchangeSignals,
  filterGroupedSignals,
} from "@shared/features/signals/common/utils";
import { useDispatch, useSelector } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  SignalColumn,
  SignalsPageData,
} from "@shared/features/signals/common/types";
import { sharedLib } from "@shared/services/shared.service";
import { pickBy } from "lodash";
import { BaseFollowSignalData, Signal, SignalDataWithKey } from "@shared/types";
import { FOLLOW_SIGNAL_DATA_KEYS_SET } from "@shared/features/signals/common/constants";
import { groupConfigurationsFilters } from "@shared/slices/filters.slice";
import { CompareFn } from "@shared/common/CardsList/types";
import {
  sortByNumberProperty,
  sortByStringProperty,
} from "@shared/utils/general";
import { MARGIN_TYPE_TO_DISPLAY } from "@shared/common/constants";
import { translateOptionName } from "@shared/common/i18n/optionNames";

export const useSignalsData = (
  isOpen?: boolean,
  isAdmin?: boolean,
  skip?: boolean
): SignalsPageData => {
  const dispatch = useDispatch();
  const filtersSlice = getSignalsFiltersSlice(isOpen, isAdmin);
  const oppositeStatusFiltersSlice = getSignalsFiltersSlice(!isOpen, isAdmin);
  const filtersData = useSelector(filtersSlice.selectors.selectFiltersData);
  const [subGroupId] = useSelector(
    (isAdmin ? groupConfigurationsFilters : filtersSlice).selectors
      .selectGroupsFilter
  );
  const [oppositeStatusSubGroupId] = useSelector(
    oppositeStatusFiltersSlice.selectors.selectGroupsFilter
  );

  const signalsArgs = useMemo(
    () =>
      subGroupId
        ? {
            for_admin: !!isAdmin,
            sub_group_id: subGroupId,
            is_open: !!isOpen,
          }
        : undefined,
    [subGroupId, isOpen, isAdmin]
  );
  const getSignalsDataQuery =
    sharedLib.sharedApi.signals.useGetSignalsDataQuery(
      skip || !signalsArgs ? skipToken : signalsArgs
    );

  useEffect(() => {
    if (subGroupId && subGroupId !== oppositeStatusSubGroupId) {
      setTimeout(() => {
        dispatch(
          oppositeStatusFiltersSlice.actions.setGroupsFilter([subGroupId])
        );
      }, 1000);
    }
  }, [subGroupId]);

  const rawSignalsData = getSignalsDataQuery.data?.signals;

  const signalsData = useMemo(() => {
    if (!rawSignalsData) return;
    const groupedSignals = groupExchangeSignals(rawSignalsData || []);
    const signalIdToAllFollowData = groupedSignals.reduce(
      (acc: Record<number, BaseFollowSignalData>, signal) => {
        acc[signal.signal_id] = pickBy(signal, (_, key) =>
          FOLLOW_SIGNAL_DATA_KEYS_SET.has(key as keyof BaseFollowSignalData)
        );
        return acc;
      },
      {}
    );
    const filteredSignals =
      filterExchangeSignals(rawSignalsData, filtersData) || [];
    const groupedFilteredExchangeSignals = groupFilteredExchangeSignals(
      filteredSignals,
      signalIdToAllFollowData
    );
    return filterGroupedSignals(groupedFilteredExchangeSignals, filtersData);
  }, [rawSignalsData, filtersData]);

  return {
    getSignalsDataQuery,
    args: signalsArgs,
    signalsData,
    filtersSlice,
  };
};

export const useSortSignalsCards = () => {
  const signalColumnToSorter: Record<SignalColumn, CompareFn<Signal>> = {
    [SignalColumn.TIME]: (a, b) => sortByNumberProperty(a, b, "relevant_time"),
    [SignalColumn.SYMBOL]: (a, b) => sortByStringProperty(a, b, "symbol"),
    [SignalColumn.POTENTIAL_PROFIT]: (a, b) => {
      const aVal = a.signal_extra_data?.["Potential Profit Ratio"] || -Infinity;
      const bVal = b.signal_extra_data?.["Potential Profit Ratio"] || -Infinity;
      return sortByNumberProperty(aVal, bVal);
    },
    [SignalColumn.PROGRESS]: (a, b) =>
      sortByNumberProperty(a, b, "graph_data.profit"),
  };

  const defaultSortFunction = useCallback(
    (a: SignalDataWithKey, b: SignalDataWithKey) =>
      b.graph_data.profit - a.graph_data.profit,
    []
  );

  return {
    signalColumnToSorter,
    defaultSortFunction,
  };
};

export const useSignalCard = () => {
  const getLeverage = (signalData: Signal) => {
    const marginType = signalData.signal_extra_data["Margin Type"];
    const leverage = signalData.signal_extra_data["Leverage"];
    const longShort = signalData.signal_extra_data["Long/Short"];
    const marginTypeStr = marginType
      ? ` - ${translateOptionName(MARGIN_TYPE_TO_DISPLAY[marginType])} `
      : "";
    const leverageDisplay = leverage ? `${leverage}X` : "";
    const margin = `${marginTypeStr}${leverageDisplay}`;

    return {
      longShort,
      margin,
    };
  };

  const getExtraInfoValues = (signalData: Signal) => {
    const { longShort, margin } = getLeverage(signalData);
    return {
      margin,
      longShort,
      group: signalData.signal_extra_data["Group"],
      timestamp: signalData.relevant_time,
      exchanges: signalData.signal_extra_data["Exchanges"]?.split(", "),
    };
  };

  return { getExtraInfoValues };
};

export const usePersonalGroups = (skip?: boolean) => {
  const { data: subGroups } =
    sharedLib.sharedApi.settings.useGetGroupsDataQuery(
      skip ? skipToken : undefined
    );

  return useMemo(
    () => subGroups?.filter((group) => group.is_personal),
    [subGroups]
  );
};

export const useIsPersonalGroup = (subGroupId?: number, skip?: boolean) => {
  const personalGroups = usePersonalGroups(skip);
  return useMemo(
    () =>
      !!subGroupId &&
      !!personalGroups?.find((group) => group.id === subGroupId),
    [personalGroups, subGroupId]
  );
};
